import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Breadcrumbs from '../../components/Breadcrumbs';
import '../../styles/PacDebt.css';
import Footer from '../../components/Footer';

// ─── Task Defaults ────────────────────────────────────────────────────────────


//const LOGS_API = 'https://eoadzicpvjk8ibh.m.pipedream.net';
const LOGS_API = 'https://eomsee0982iqc2u.m.pipedream.net/api/BTC/GetAllLogs';

// ─── CSV Parser ───────────────────────────────────────────────────────────────

const parseCSVLine = (line) => {
  const fields = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { field += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      fields.push(field.trim());
      field = '';
    } else {
      field += ch;
    }
  }
  fields.push(field.trim());
  return fields;
};

const parseCSVToRows = (text, level) => {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) throw new Error('CSV must contain at least one data row.');

  const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));

  const required = level === 'debt'
    ? ['contact_id', 'debt_id', 'task_description']
    : ['contact_id', 'task_description'];

  const missing = required.filter(r => !headers.includes(r));
  if (missing.length) throw new Error(`Missing required column(s): ${missing.map(c => c.replace(/_/g, ' ')).join(', ')}`);

  const get = (vals, key) => vals[headers.indexOf(key)] || '';

  return lines.slice(1)
    .map(line => {
      const vals = parseCSVLine(line);
      const row = {
        contactId:   get(vals, 'contact_id'),
        debtId:      get(vals, 'debt_id'),
        description: get(vals, 'task_description'),
        assignedTo:  get(vals, 'assigned_to'),
        dueDate:     get(vals, 'due_date'),
        taskCreated: null,
        noteLogged:  null,
        status:      'PENDING',
      };

      // Track missing required fields so the preview table can highlight them
      const missingFields = [];
      if (!row.contactId)   missingFields.push('contactId');
      if (level === 'debt' && !row.debtId) missingFields.push('debtId');
      if (!row.description) missingFields.push('description');

      if (missingFields.length) {
        row.status        = 'INVALID';
        row.missingFields = missingFields;
      }

      return row;
    })
    // keep any row that has at least one populated field so the user can see what's missing
    .filter(r => r.contactId || r.debtId || r.description || r.assignedTo || r.dueDate);
};

//const UPLOAD_API = 'https://eo5w9ryit80etd8.m.pipedream.net';
const UPLOAD_API = 'https://eosi5oo9xakvhz2.m.pipedream.net/api/BTC/CreateTask';

const toISODate = (str) => {
  if (!str) return '';
  if (str.includes('-')) return str;                          // already YYYY-MM-DD
  const [m, d, y] = str.split('/');
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`; // MM/DD/YYYY → YYYY-MM-DD
};

// ─── Small helpers ────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const map = {
    SUCCESS: ['#dcfce7', '#166534'],
    ERROR:   ['#fee2e2', '#991b1b'],
    PENDING: ['#fef9c3', '#854d0e'],
    INVALID: ['#fee2e2', '#991b1b'],
  };
  const [bg, color] = map[status] || ['#f3f4f6', '#6b7280'];
  return (
    <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: bg, color, fontWeight: 500 }}>
      {status}
    </span>
  );
};

const Dot = ({ success }) => (
  <span style={{
    width: 7, height: 7, borderRadius: '50%', display: 'inline-block', marginRight: 5,
    background: success === true ? '#16a34a' : success === false ? '#dc2626' : '#d1d5db',
  }} />
);

const Check = ({ val }) => (
  <span style={{ color: val ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{val ? '✓' : '✗'}</span>
);

const InfoBar = ({ children }) => (
  <div style={{
    background: '#f0f7ff', border: '0.5px solid #bfdbfe', borderRadius: '8px',
    padding: '10px 14px', fontSize: '12px', color: '#1e40af',
    marginBottom: '20px', display: 'flex', gap: '8px', alignItems: 'flex-start',
  }}>
    <span>ℹ</span>
    <span>{children}</span>
  </div>
);

const TableWrap = ({ children }) => (
  <div style={{ border: '0.5px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
      {children}
    </table>
  </div>
);

const TH = ({ children }) => (
  <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 500, color: '#374151', borderBottom: '0.5px solid #e5e7eb', whiteSpace: 'nowrap', background: '#f9fafb' }}>
    {children}
  </th>
);

const TD = ({ children, truncate, missing, editable, value, onChange, placeholder }) => {
  // Editable cell — used for required fields in the preview table so users can
  // fix missing or incorrect values inline before uploading.
  if (editable) {
    return (
      <td style={{
        padding: '5px 8px',
        background: missing ? '#fef2f2' : 'transparent',
        borderBottom: '0.5px solid #f3f4f6',
      }}>
        <input
          type="text"
          value={value ?? ''}
          onChange={onChange}
          placeholder={placeholder || (missing ? '⚠ required' : '')}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '5px 7px',
            fontSize: '12px',
            border: '1px solid',
            borderColor: missing ? '#dc2626' : '#e5e7eb',
            borderRadius: '4px',
            background: '#fff',
            color: missing ? '#991b1b' : '#111827',
            outline: 'none',
            fontWeight: missing ? 500 : 400,
            transition: 'border-color 0.15s, background 0.15s',
          }}
          onFocus={(e) => { e.target.style.borderColor = '#2563eb'; }}
          onBlur={(e) => { e.target.style.borderColor = missing ? '#dc2626' : '#e5e7eb'; }}
        />
      </td>
    );
  }

  return (
    <td style={{
      padding: '9px 12px',
      color: missing ? '#991b1b' : '#374151',
      background: missing ? '#fee2e2' : 'transparent',
      borderBottom: '0.5px solid #f3f4f6',
      fontWeight: missing ? 500 : 400,
      ...(truncate ? { maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
    }}>
      {missing
        ? <span style={{ fontStyle: 'italic' }}>⚠ missing</span>
        : children}
    </td>
  );
};

const UploadingModal = ({ open, total, completed }) => {
  if (!open) return null;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '28px 32px',
        width: '90%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        textAlign: 'center',
      }}>
        <div style={{
          width: 44, height: 44, margin: '0 auto 14px',
          border: '4px solid #e5e7eb', borderTop: '4px solid #0171BB',
          borderRadius: '50%', animation: 'btc-spin 0.9s linear infinite',
        }} />
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827', marginBottom: '6px' }}>
          Uploading data…
        </div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '14px' }}>
          Please wait while your tasks are being created. Do not close this window.
        </div>
        {total > 0 && (
          <>
            <div style={{
              height: 8, background: '#f3f4f6', borderRadius: '999px', overflow: 'hidden', marginBottom: '8px',
            }}>
              <div style={{
                width: `${pct}%`, height: '100%', background: '#0171BB', transition: 'width 0.2s',
              }} />
            </div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>
              {completed} of {total} processed ({pct}%)
            </div>
          </>
        )}
        <style>{`@keyframes btc-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

const EmptyRow = ({ cols }) => (
  <tr><td colSpan={cols} style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>
    No data uploaded yet. Upload a CSV file to see results here.
  </td></tr>
);


// ─── Tab components ───────────────────────────────────────────────────────────

const ContactTab = ({ groups, isLoading, teams, isLoadingTeams, users, isLoadingUsers }) => {
  const [taskType, setTaskType]         = useState('');
  const [assignType, setAssignType]     = useState('team');
  const [assignedTo, setAssignedTo]     = useState('');
  const [dueDate, setDueDate]           = useState('');
  const [rows, setRows]                 = useState(null);
  const [file, setFile]                 = useState(null);
  const [previewed, setPreviewed]       = useState(false);
  const [uploaded, setUploaded]         = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [isUploading, setIsUploading]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });

  // Derive group id, task id, and task name from encoded select value "groupId|taskId|taskName"
  const [groupId = '', taskId = '', ...nameParts] = taskType.split('|');
  const taskName = nameParts.join('|');
  const teamName = groups.find(g => String(g.group_id) === groupId)?.group_name || '';

  const filteredGroups = groups.filter(g => g.group_name && g.task_types.some(t => t.task_type === taskName));
  const teamList = teams;
  const assignedToDisplay = assignedTo
    ? (assignType === 'team'
        //? groups.find(g => String(g.group_id) === String(assignedTo))?.group_name
        ? teamList.find(t => String(t.team_id) === String(assignedTo))?.team_name
        : users.find(u => String(u.userId) === String(assignedTo))?.name) || assignedTo
    : null;

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setPreviewed(false);
    setUploaded(false);
    setRows(null);
    setPreviewError('');
  };

  const handlePreview = () => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCSVToRows(e.target.result, 'contact');
        setRows(parsed);
        setPreviewed(true);
        setUploaded(false);
        setPreviewError('');
      } catch (err) {
        setPreviewError(err.message);
        setRows(null);
        setPreviewed(false);
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!rows || !rows.length) return;

    console.log("Check Rows Payload for Contact: ", rows)
    const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; } })();
    const uploadedBy = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'Unknown';
    setIsUploading(true);
    setUploadProgress({ completed: 0, total: rows.length });
    try {
      const updated = await Promise.all(
        rows.map(async (row) => {
          const payload = {
            contact_id:    row.contactId ? parseInt(row.contactId, 10) : null,
            task_name:     taskName,
            task_type:     taskName,
            defined_task:  parseInt(taskId, 10),
            task_due_date: dueDate || toISODate(row.dueDate),
            task_note:     row.description,
            title:         taskName,
            assigned_to:   assignedTo ? { type: assignType, id: assignedTo, name: assignedToDisplay } : null,
            level:         'Contact',
            uploaded_by:   uploadedBy,
            team_name:     teamName,
          };
          try {
            const res = await fetch(UPLOAD_API, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            const ok = res.ok;
            setUploadProgress((p) => ({ ...p, completed: p.completed + 1 }));
            return { ...row, taskCreated: ok, noteLogged: ok, status: ok ? 'SUCCESS' : 'ERROR' };
          } catch {
            setUploadProgress((p) => ({ ...p, completed: p.completed + 1 }));
            return { ...row, taskCreated: false, noteLogged: false, status: 'ERROR' };
          }
        })
      );
      const successful = updated.filter(r => r.status === 'SUCCESS').length;
      const errors     = updated.filter(r => r.status === 'ERROR').length;
      fetch(LOGS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          log_summary:    true,
          uploaded_by:    uploadedBy,
          task_type:      taskName,
          level:          'Contact',
          team_name:      teamName,
          assigned_to:    assignedTo ? { type: assignType, id: assignedTo, name: assignedToDisplay } : null,
          total_records:  rows.length,
          successful,
          errors,
          timestamp:      new Date().toLocaleString(),
        }),
      }).catch(() => {});
      setRows(updated);
      setUploaded(true);
    } finally {
      setIsUploading(false);
    }
  };

  const s = { height: 36, border: '0.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', padding: '0 10px', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer', minWidth: 260 };

  const invalidCount = rows ? rows.filter(r => r.status === 'INVALID').length : 0;

  // Inline-edit handler for the preview table. Re-validates the row's required
  // fields after each keystroke so the red highlight, status badge, and invalid
  // count update live as the user types.
  const updateRowField = (index, field) => (e) => {
    const value = e.target.value;
    setRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      const missingFields = [];
      if (!updated.contactId)   missingFields.push('contactId');
      if (!updated.description) missingFields.push('description');
      if (missingFields.length) {
        updated.missingFields = missingFields;
        updated.status        = 'INVALID';
      } else {
        delete updated.missingFields;
        updated.status = 'PENDING';
      }
      next[index] = updated;
      return next;
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>Task Type</label>
          <select value={taskType} onChange={(e) => { setTaskType(e.target.value); setAssignedTo(''); }} style={s} disabled={isLoading}>
            <option value="">{isLoading ? 'Loading...' : '-- Select a task type --'}</option>
            {groups.map((g) => (
              <optgroup key={g.group_id} label={g.group_name || 'Other'}>
                {g.task_types.map((t) => (
                  <option key={t.task_id} value={`${g.group_id}|${t.task_id}|${t.task_type}`}>{t.task_type}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>Assigned To</label>
            <AssignToggle value={assignType} onChange={(v) => { setAssignType(v); setAssignedTo(''); }} />
          </div>
          <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} style={s} disabled={isLoading || isLoadingUsers || !taskType}>
            <option value="">{!taskType ? '-- Select a task type first --' : '-- Use task default --'}</option>
            {assignType === 'team'
              // ? filteredGroups.map((g) => (
              //     <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
              //   ))
              ? [...teamList]
              .sort((a, b) => a.team_name.localeCompare(b.team_name))
              .map((t) => (
                  <option key={t.team_id} value={t.team_id}>{t.team_name}</option>
              ))
              : (isLoadingUsers
                  ? [<option key="loading" disabled>Loading users...</option>]
                  : users
                      .filter(u => u.userId !== '--')
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((u) => (
                        <option key={u.userId} value={u.userId}>{u.name} ({u.userId})</option>
                      ))
                )
            }
          </select>
        </div>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>Due Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ height: 36, border: '0.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', padding: '0 10px', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer' }}
          />
        </div>
      </div>


      <InfoBar>
        <strong>Required:</strong> Select a <strong>Task Type</strong>, <strong>Assigned To</strong>, and <strong>Due Date</strong> above. CSV must include <strong>Contact ID</strong> and <strong>Task Description</strong> columns.
      </InfoBar>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>Upload CSV</label>
          <input type="file" accept=".csv" onChange={handleFileChange} style={{ height: 36, border: '0.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', minWidth: 260, color: '#111827' }} />
        </div>
        <Btn onClick={handlePreview} disabled={!file}>Preview CSV</Btn>
        <Btn primary onClick={handleUpload} disabled={!previewed || uploaded || !taskType || !assignedTo || !dueDate}>Upload &amp; Create Tasks</Btn>
      </div>

      {previewed && !uploaded && (!taskType || !assignedTo || !dueDate) && (
        <div style={{ color: '#92400e', background: '#fffbeb', border: '0.5px solid #fcd34d', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ Please fill in all required fields before uploading:{' '}
          {[!taskType && 'Task Type', !assignedTo && 'Assigned To', !dueDate && 'Due Date'].filter(Boolean).join(', ')}.
        </div>
      )}

      {previewed && !uploaded && invalidCount > 0 && (
        <div style={{ color: '#991b1b', background: '#fee2e2', border: '0.5px solid #fca5a5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ {invalidCount} row{invalidCount === 1 ? '' : 's'} {invalidCount === 1 ? 'is' : 'are'} missing required field(s) (highlighted in red below). You can edit the highlighted cells to fix them — rows uploaded with missing fields may be rejected by the backend.
        </div>
      )}

      {previewError && (
        <div style={{ color: '#991b1b', background: '#fee2e2', border: '0.5px solid #fca5a5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ {previewError}
        </div>
      )}

      <hr style={{ border: 'none', borderTop: '0.5px solid #e5e7eb', margin: '12px 0 20px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
          {uploaded ? 'Upload Results' : previewed ? 'Preview Results' : 'Upload Results'}
        </span>
        <Badge>{rows ? `${rows.length} records` : '0 records'}</Badge>
        {rows && rows.some(r => r.status === 'SUCCESS') && <Badge success>{rows.filter(r => r.status === 'SUCCESS').length} success</Badge>}
        {rows && rows.some(r => r.status === 'ERROR')   && <Badge error>{rows.filter(r => r.status === 'ERROR').length} error</Badge>}
        {rows && rows.some(r => r.status === 'INVALID') && <Badge error>{invalidCount} invalid</Badge>}
        {rows && rows.some(r => r.status === 'PENDING') && <Badge>{rows.filter(r => r.status === 'PENDING').length} pending review</Badge>}
      </div>

      <TableWrap>
        <thead style={{ background: '#f9fafb' }}><tr>
          <TH>Contact ID</TH><TH>Task Description</TH><TH>Assigned To</TH>
          <TH>Due Date</TH><TH>Task Created</TH><TH>Note Logged</TH><TH>Status</TH>
        </tr></thead>
        <tbody>
          {!rows ? <EmptyRow cols={7} /> : rows.map((r, i) => (
            <tr key={i}>
              <TD
                editable={!uploaded}
                value={r.contactId}
                missing={!r.contactId}
                onChange={updateRowField(i, 'contactId')}
                placeholder="Contact ID"
              >{r.contactId}</TD>
              <TD
                editable={!uploaded}
                value={r.description}
                missing={!r.description}
                onChange={updateRowField(i, 'description')}
                placeholder="Task description"
                truncate
              >{r.description}</TD>
              <TD>{assignedToDisplay || r.assignedTo || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>default</span>}</TD>
              <TD>{dueDate || r.dueDate || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>default SLA</span>}</TD>
              <TD><Check val={r.taskCreated} /></TD>
              <TD><Check val={r.noteLogged} /></TD>
              <TD><StatusBadge status={r.status} /></TD>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <FooterTip>Tip: Missing required fields can be edited inline before upload. <strong>Required fields:</strong> Contact ID, Task Description</FooterTip>
      <UploadingModal open={isUploading} total={uploadProgress.total} completed={uploadProgress.completed} />
    </div>
  );
};

const DebtTab = ({ groups, isLoading, teams, isLoadingTeams, users, isLoadingUsers }) => {
  const [taskType, setTaskType]         = useState('');
  const [assignType, setAssignType]     = useState('team');
  const [assignedTo, setAssignedTo]     = useState('');
  const [dueDate, setDueDate]           = useState('');
  const [rows, setRows]                 = useState(null);
  const [file, setFile]                 = useState(null);
  const [previewed, setPreviewed]       = useState(false);
  const [uploaded, setUploaded]         = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [isUploading, setIsUploading]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ completed: 0, total: 0 });

  // Derive group id, task id, and task name from encoded select value "groupId|taskId|taskName"
  const [groupId = '', taskId = '', ...nameParts] = taskType.split('|');
  const taskName = nameParts.join('|');
  const teamName = groups.find(g => String(g.group_id) === groupId)?.group_name || '';

  const filteredGroups = groups.filter(g => g.group_name && g.task_types.some(t => t.task_type === taskName));
  const teamList = teams;
  const assignedToDisplay = assignedTo
    ? (assignType === 'team'
        //? groups.find(g => String(g.group_id) === String(assignedTo))?.group_name
        ? teamList.find(t => String(t.team_id) === String(assignedTo))?.team_name
        : users.find(u => String(u.userId) === String(assignedTo))?.name) || assignedTo
    : null;

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setPreviewed(false);
    setUploaded(false);
    setRows(null);
    setPreviewError('');
  };

  const handlePreview = () => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseCSVToRows(e.target.result, 'debt');
        setRows(parsed);
        setPreviewed(true);
        setUploaded(false);
        setPreviewError('');
      } catch (err) {
        setPreviewError(err.message);
        setRows(null);
        setPreviewed(false);
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!rows || !rows.length) return;

    console.log("Check Rows Payload for Debt: ", rows)
    const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; } })();
    const uploadedBy = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || 'Unknown';
    setIsUploading(true);
    setUploadProgress({ completed: 0, total: rows.length });
    try {
      const updated = await Promise.all(
        rows.map(async (row) => {
          const payload = {
            contact_id:    row.contactId ? parseInt(row.contactId, 10) : null,
            debt_id:       row.debtId || null,
            task_name:     taskName,
            task_type:     taskName,
            defined_task:  parseInt(taskId, 10),
            task_due_date: dueDate || toISODate(row.dueDate),
            task_note:     row.description,
            title:         taskName,
            assigned_to:   assignedTo ? { type: assignType, id: assignedTo, name: assignedToDisplay } : null,
            level:         'Debt',
            uploaded_by:   uploadedBy,
            team_name:     teamName,
          };
          try {
            const res = await fetch(UPLOAD_API, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            const ok = res.ok;
            setUploadProgress((p) => ({ ...p, completed: p.completed + 1 }));
            return { ...row, taskCreated: ok, noteLogged: ok, status: ok ? 'SUCCESS' : 'ERROR' };
          } catch {
            setUploadProgress((p) => ({ ...p, completed: p.completed + 1 }));
            return { ...row, taskCreated: false, noteLogged: false, status: 'ERROR' };
          }
        })
      );
      const successful = updated.filter(r => r.status === 'SUCCESS').length;
      const errors     = updated.filter(r => r.status === 'ERROR').length;
      fetch(LOGS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          log_summary:    true,
          uploaded_by:    uploadedBy,
          task_type:      taskName,
          level:          'Debt',
          team_name:      teamName,
          assigned_to:    assignedTo ? { type: assignType, id: assignedTo, name: assignedToDisplay } : null,
          total_records:  rows.length,
          successful,
          errors,
          timestamp:      new Date().toLocaleString(),
        }),
      }).catch(() => {});
      setRows(updated);
      setUploaded(true);
    } finally {
      setIsUploading(false);
    }
  };

  const s = { height: 36, border: '0.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', padding: '0 10px', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer', minWidth: 260 };

  const invalidCount = rows ? rows.filter(r => r.status === 'INVALID').length : 0;

  // Inline-edit handler for the preview table — re-validates contactId, debtId,
  // and description on each keystroke so the row's status badge and red
  // highlight update live as the user fills missing fields.
  const updateRowField = (index, field) => (e) => {
    const value = e.target.value;
    setRows((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      const missingFields = [];
      if (!updated.contactId)   missingFields.push('contactId');
      if (!updated.debtId)      missingFields.push('debtId');
      if (!updated.description) missingFields.push('description');
      if (missingFields.length) {
        updated.missingFields = missingFields;
        updated.status        = 'INVALID';
      } else {
        delete updated.missingFields;
        updated.status = 'PENDING';
      }
      next[index] = updated;
      return next;
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>Task Type</label>
          <select value={taskType} onChange={(e) => { setTaskType(e.target.value); setAssignedTo(''); }} style={s} disabled={isLoading}>
            <option value="">{isLoading ? 'Loading...' : '-- Select a task type --'}</option>
            {groups.map((g) => (
              <optgroup key={g.group_id} label={g.group_name || 'Other'}>
                {g.task_types.map((t) => (
                  <option key={t.task_id} value={`${g.group_id}|${t.task_id}|${t.task_type}`}>{t.task_type}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151' }}>Assigned To</label>
            <AssignToggle value={assignType} onChange={(v) => { setAssignType(v); setAssignedTo(''); }} />
          </div>
          <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} style={s} disabled={isLoading || isLoadingUsers || !taskType}>
            <option value="">{!taskType ? '-- Select a task type first --' : '-- Use task default --'}</option>
            {assignType === 'team'
              // ? filteredGroups.map((g) => (
              //     <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
              //   ))
              ? [...teamList]
              .sort((a, b) => a.team_name.localeCompare(b.team_name))
              .map((t) => (
                  <option key={t.team_id} value={t.team_id}>{t.team_name}</option>
              ))
              : (isLoadingUsers
                  ? [<option key="loading" disabled>Loading users...</option>]
                  : users
                      .filter(u => u.userId !== '--')
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((u) => (
                        <option key={u.userId} value={u.userId}>{u.name} ({u.userId})</option>
                      ))
                )
            }
          </select>
        </div>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>Due Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ height: 36, border: '0.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', padding: '0 10px', background: '#fff', color: '#111827', outline: 'none', cursor: 'pointer' }}
          />
        </div>
      </div>


      <InfoBar>
        <strong>Required:</strong> Select a <strong>Task Type</strong>, <strong>Assigned To</strong>, and <strong>Due Date</strong> above. CSV must include <strong>Contact ID</strong>, <strong>Debt ID</strong>, and <strong>Task Description</strong> columns.
      </InfoBar>

      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>Upload CSV</label>
          <input type="file" accept=".csv" onChange={handleFileChange} style={{ height: 36, border: '0.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', minWidth: 260, color: '#111827' }} />
        </div>
        <Btn onClick={handlePreview} disabled={!file}>Preview CSV</Btn>
        <Btn primary onClick={handleUpload} disabled={!previewed || uploaded || !taskType || !assignedTo || !dueDate}>Upload &amp; Create Tasks</Btn>
      </div>

      {previewed && !uploaded && (!taskType || !assignedTo || !dueDate) && (
        <div style={{ color: '#92400e', background: '#fffbeb', border: '0.5px solid #fcd34d', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ Please fill in all required fields before uploading:{' '}
          {[!taskType && 'Task Type', !assignedTo && 'Assigned To', !dueDate && 'Due Date'].filter(Boolean).join(', ')}.
        </div>
      )}

      {previewed && !uploaded && invalidCount > 0 && (
        <div style={{ color: '#991b1b', background: '#fee2e2', border: '0.5px solid #fca5a5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ {invalidCount} row{invalidCount === 1 ? '' : 's'} {invalidCount === 1 ? 'is' : 'are'} missing required field(s) (highlighted in red below). You can edit the highlighted cells to fix them — rows uploaded with missing fields may be rejected by the backend.
        </div>
      )}

      {previewError && (
        <div style={{ color: '#991b1b', background: '#fee2e2', border: '0.5px solid #fca5a5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ {previewError}
        </div>
      )}

      <hr style={{ border: 'none', borderTop: '0.5px solid #e5e7eb', margin: '12px 0 20px' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
          {uploaded ? 'Upload Results' : previewed ? 'Preview Results' : 'Upload Results'}
        </span>
        <Badge>{rows ? `${rows.length} records` : '0 records'}</Badge>
        {rows && rows.some(r => r.status === 'SUCCESS') && <Badge success>{rows.filter(r => r.status === 'SUCCESS').length} success</Badge>}
        {rows && rows.some(r => r.status === 'ERROR')   && <Badge error>{rows.filter(r => r.status === 'ERROR').length} error</Badge>}
        {rows && rows.some(r => r.status === 'INVALID') && <Badge error>{invalidCount} invalid</Badge>}
        {rows && rows.some(r => r.status === 'PENDING') && <Badge>{rows.filter(r => r.status === 'PENDING').length} pending review</Badge>}
      </div>

      <TableWrap>
        <thead style={{ background: '#f9fafb' }}><tr>
          <TH>Contact ID</TH><TH>Debt ID</TH><TH>Task Description</TH><TH>Assigned To</TH>
          <TH>Due Date</TH><TH>Task Created</TH><TH>Note Logged</TH><TH>Status</TH>
        </tr></thead>
        <tbody>
          {!rows ? <EmptyRow cols={8} /> : rows.map((r, i) => (
            <tr key={i}>
              <TD
                editable={!uploaded}
                value={r.contactId}
                missing={!r.contactId}
                onChange={updateRowField(i, 'contactId')}
                placeholder="Contact ID"
              >{r.contactId}</TD>
              <TD
                editable={!uploaded}
                value={r.debtId}
                missing={!r.debtId}
                onChange={updateRowField(i, 'debtId')}
                placeholder="Debt ID"
              >{r.debtId}</TD>
              <TD
                editable={!uploaded}
                value={r.description}
                missing={!r.description}
                onChange={updateRowField(i, 'description')}
                placeholder="Task description"
                truncate
              >{r.description}</TD>
              <TD>{assignedToDisplay || r.assignedTo || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>default</span>}</TD>
              <TD>{dueDate || r.dueDate || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>default SLA</span>}</TD>
              <TD><Check val={r.taskCreated} /></TD>
              <TD><Check val={r.noteLogged} /></TD>
              <TD><StatusBadge status={r.status} /></TD>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <FooterTip>Tip: Missing required fields can be edited inline before upload. <strong>Required fields:</strong> Contact ID, Debt ID, Task Description</FooterTip>
      <UploadingModal open={isUploading} total={uploadProgress.total} completed={uploadProgress.completed} />
    </div>
  );
};

const DirectoryTab = ({ users, isLoadingUsers }) => (
  <div>
    <InfoBar>
      This directory reflects active Forth users and their unique User IDs. All user changes, including team assignments and movements, must be updated directly in <strong>Forth</strong> — this directory is read from Forth in real time.
    </InfoBar>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
      <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>Active User Directory</span>
      <Badge>{isLoadingUsers ? 'Loading...' : `${users.length} users shown`}</Badge>
    </div>
    <TableWrap>
      <thead style={{ background: '#f9fafb' }}><tr>
        <TH>Display Name</TH><TH>Forth User ID</TH><TH>Team(s)</TH><TH>Role</TH><TH>Status</TH>
      </tr></thead>
      <tbody>
        {isLoadingUsers
          ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: '13px' }}>Loading users...</td></tr>
          : users.length === 0
            ? <EmptyRow cols={5} />
            : users
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((u, i) => (
                <tr key={i}>
                  <TD>{u.name}</TD>
                  <TD>{u.userId}</TD>
                  <TD>{u.team}</TD>
                  <TD>{u.role}</TD>
                  <TD><Dot success={u.active} />Active</TD>
                </tr>
              ))
        }
      </tbody>
    </TableWrap>
  </div>
);

const excelSerialToDate = (serial) => {
  if (!serial || isNaN(Number(serial))) return serial;
  const date = new Date(Math.round((Number(serial) - 25569) * 86400 * 1000));
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const LogDetailModal = ({ log, onClose }) => {
  if (!log) return null;
  const isDebt = log.level === 'Debt';
  const rows = Array.isArray(log.records) ? log.records : [];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: '12px', width: '95%', maxWidth: 1100,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{log.task_type || log.task_name}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {excelSerialToDate(log.timestamp)} &nbsp;·&nbsp; Uploaded by <strong>{log.uploaded_by || '—'}</strong> &nbsp;·&nbsp; {log.level} Level
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>✕</button>
        </div>
        {/* Summary bar */}
        <div style={{ padding: '10px 20px', borderBottom: '0.5px solid #e5e7eb', display: 'flex', gap: '24px', fontSize: '12px', background: '#f9fafb' }}>
          {[
            ['Records', log.total_records ?? rows.length],
            ['Successful', log.successful ?? '—'],
            ['Errors', log.errors ?? '—'],
            ['Task Due', excelSerialToDate(log.task_due_date) || '—'],
            ['Assigned To', log.assigned_to?.name || '—'],
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
              <span style={{ fontWeight: 500, color: '#111827' }}>{val}</span>
            </div>
          ))}
        </div>
        {/* Records table */}
        <div style={{ overflowY: 'auto', padding: '16px 20px' }}>
          <TableWrap>
            <thead style={{ background: '#f9fafb' }}><tr>
              <TH>#</TH>
              <TH>Contact ID</TH>
              {isDebt && <TH>Debt ID</TH>}
              <TH>Task Name</TH>
              <TH>Task Description</TH>
              <TH>Due Date</TH>
              <TH>Assigned To</TH>
              <TH>Status</TH>
            </tr></thead>
            <tbody>
              {rows.length === 0
                ? <EmptyRow cols={isDebt ? 8 : 7} />
                : rows.map((r, i) => (
                    <tr key={i}>
                      <TD>{i + 1}</TD>
                      <TD>{r.contact_id || '—'}</TD>
                      {isDebt && <TD>{r.debt_id || '—'}</TD>}
                      <TD>{r.task_name || r.title || '—'}</TD>
                      <TD truncate>{r.task_note || '—'}</TD>
                      <TD>{excelSerialToDate(r.task_due_date) || '—'}</TD>
                      <TD>{r.assigned_to?.name || r.assigned_to?.id || '—'}</TD>
                      <TD><StatusBadge status={r.successful === 1 || r.successful === true ? 'SUCCESS' : 'ERROR'} /></TD>
                    </tr>
                  ))
              }
            </tbody>
          </TableWrap>
        </div>
      </div>
    </div>
  );
};

const LogsTab = () => {
  const [logs, setLogs]         = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch(LOGS_API)
      .then((r) => r.json())
      .then((data) => setLogs(Array.isArray(data?.uploads) ? data.uploads : []))
      .catch(() => setError('Failed to load historical logs.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>Historical Upload Logs</span>
        {!isLoading && <Badge>{logs.length} uploads</Badge>}
      </div>
      {error && (
        <div style={{ color: '#991b1b', background: '#fee2e2', border: '0.5px solid #fca5a5', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', marginBottom: '12px' }}>
          ⚠ {error}
        </div>
      )}
      <TableWrap>
        <thead style={{ background: '#f9fafb' }}><tr>
          <TH>Date / Time</TH><TH>Uploaded By</TH><TH>Task Type</TH><TH>Level</TH>
          <TH>Records</TH><TH>Successful</TH><TH>Errors</TH><TH>Details</TH>
        </tr></thead>
        <tbody>
          {isLoading
            ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af', fontSize: '13px' }}>Loading logs...</td></tr>
            : logs.length === 0
              ? <EmptyRow cols={8} />
              : logs.map((l, i) => (
                  <tr key={i}>
                    <TD>{excelSerialToDate(l.timestamp)}</TD>
                    <TD>{l.uploaded_by}</TD>
                    <TD>{l.task_type}</TD>
                    <TD>{l.level}</TD>
                    <TD>{l.total_records}</TD>
                    <TD><Dot success={true} />{l.successful}</TD>
                    <TD>{l.errors > 0 ? <><Dot success={false} />{l.errors}</> : l.errors}</TD>
                    <TD><Btn small onClick={() => setSelected(l)}>View</Btn></TD>
                  </tr>
                ))
          }
        </tbody>
      </TableWrap>
      <LogDetailModal log={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

// ─── Shared small components ──────────────────────────────────────────────────

const Btn = ({ children, onClick, primary, small, disabled }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height: small ? 30 : 36,
        padding: small ? '0 12px' : '0 16px',
        borderRadius: '8px',
        fontSize: small ? '12px' : '13px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: '0.5px solid',
        borderColor: primary ? '#0171BB' : '#d1d5db',
        background: disabled ? '#e5e7eb' : primary ? (hovered ? '#015fa0' : '#0171BB') : (hovered ? '#f9fafb' : '#fff'),
        color: disabled ? '#9ca3af' : primary ? '#fff' : '#374151',
        opacity: disabled ? 0.7 : 1,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
        alignSelf: 'flex-end',
      }}
    >{children}</button>
  );
};

const Badge = ({ children, success, error }) => {
  const bg = success ? '#dcfce7' : error ? '#fee2e2' : '#f3f4f6';
  const color = success ? '#166534' : error ? '#991b1b' : '#6b7280';
  return <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: bg, color }}>{children}</span>;
};

const FooterTip = ({ children }) => (
  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '0.5px solid #f3f4f6', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
    {children}
  </div>
);

const AssignToggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', border: '0.5px solid #d1d5db', borderRadius: '6px', overflow: 'hidden', fontSize: '11px' }}>
    {['team', 'user'].map((v) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        style={{
          padding: '3px 10px',
          border: 'none',
          cursor: 'pointer',
          background: value === v ? '#2563eb' : '#f9fafb',
          color: value === v ? '#fff' : '#6b7280',
          fontWeight: value === v ? 600 : 400,
          transition: 'all 0.15s',
          textTransform: 'capitalize',
        }}
      >{v}</button>
    ))}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'contact',   label: 'Contact-Level Tasks' },
  { id: 'debt',      label: 'Debt-Level Tasks' },
  { id: 'directory', label: 'User Directory' },
  { id: 'logs',      label: 'Historical Logs' },
];

// const GROUPS_API = 'https://eoos6emsx5xuvxp.m.pipedream.net';
// const USERS_API  = 'https://eod0ld1bv24gpde.m.pipedream.net';

const GROUPS_API = 'https://eoxu8xwrj3jhlgi.m.pipedream.net/api/BTC/GetAllGroups'; 
const TEAMS_API = 'https://eoamqd84vsl6b0b.m.pipedream.net/api/BTC/GetAllTeams'; 
const USERS_API  = 'https://eow1ru14n71s7m4.m.pipedream.net/api/BTC/GetAllUsers';

const BulkTaskCreator = () => {
  const [activeTab, setActiveTab]         = useState('contact');
  const [groups, setGroups]               = useState([]);
  const [isLoadingGroups, setIsLoading]   = useState(true);
  const [teams, setTeams]               = useState([]);
  const [isLoadingTeams, setLoadingTeams]   = useState(true);
  const [users, setUsers]                 = useState([]);
  const [isLoadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    fetch(GROUPS_API)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) ? setGroups(data) : setGroups([]))
      .catch(() => setGroups([]))
      .finally(() => setIsLoading(false));
  }, []);

   useEffect(() => {
    fetch(TEAMS_API)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) ? setTeams(data) : setTeams([]))
      .catch(() => setTeams([]))
      .finally(() => setLoadingTeams(false));
  }, []);

  useEffect(() => {
    fetch(USERS_API, { headers: { 'Accept': 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        console.log('[USERS API] raw response:', data);
        if (Array.isArray(data)) {
          console.log('[USERS API] first record keys:', Object.keys(data[0]));
          const normalized = data
            .filter(u => u.name && u.name.trim())
            .map(u => ({
              userId: u.user_id,
              name: u.name.trim(),
              team: Array.isArray(u.teams) ? u.teams.map(t => t.trim()).join(', ') : '—',
              role: u.role || '—',
              active: true,
            }));
          console.log('[USERS API] normalized[0]:', normalized[0]);
          setUsers(normalized);
        } else {
          console.log('[USERS API] not an array:', typeof data);
          setUsers([]);
        }
      })
      .catch((err) => { console.error('[USERS API] fetch error:', err); setUsers([]); })
      .finally(() => setLoadingUsers(false));
  }, []);

  return (
    <StyledContainer>
      <div className="app-container">
        <div className="container">

          <h1>Bulk Task Creator</h1>
          <Breadcrumbs />

          {/* Tab Navigation */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px 8px 0 0',
            border: '1px solid #e5e7eb',
            borderBottom: 'none',
            display: 'flex',
            marginTop: '20px',
          }}>
            {TABS.map((tab, i) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  borderRadius: i === 0 ? '8px 0 0 0' : i === TABS.length - 1 ? '0 8px 0 0' : '0',
                  cursor: 'pointer',
                  backgroundColor: activeTab === tab.id ? '#2563eb' : '#f9fafb',
                  color: activeTab === tab.id ? 'white' : '#6b7280',
                  borderRight: i < TABS.length - 1 ? '1px solid #e5e7eb' : 'none',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0 0 8px 8px',
            border: '1px solid #e5e7eb',
            padding: '24px 20px',
            minHeight: '600px',
          }}>
            {activeTab === 'contact'   && <ContactTab groups={groups} isLoading={isLoadingGroups} teams={teams} isLoadingTeams={isLoadingTeams}  users={users} isLoadingUsers={isLoadingUsers} />}
            {activeTab === 'debt'      && <DebtTab groups={groups} isLoading={isLoadingGroups}  teams={teams} isLoadingTeams={isLoadingTeams} users={users} isLoadingUsers={isLoadingUsers} />}
            {activeTab === 'directory' && <DirectoryTab users={users} isLoadingUsers={isLoadingUsers} />}
            {activeTab === 'logs'      && <LogsTab />}
          </div>

          {/* Status Bar */}
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb',
            padding: '12px 16px', marginTop: '12px', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#6b7280',
          }}>
            <div><strong>Active Tab:</strong>
              <span style={{ marginLeft: '8px', padding: '2px 8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontWeight: '500' }}>
                {TABS.find(t => t.id === activeTab)?.label}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>Bulk operations are logged and auditable</div>
          </div>
          <div className='custom-footer'>
              <Footer/>
          </div>
        </div>
      </div>
    </StyledContainer>
  );
};

export default BulkTaskCreator;

const StyledContainer = styled.div`
  .spanText {
    padding-right: 10px;
    font-size: 20px;
  }
`;